import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    const itemCount = Number(event.detail?.resource?.item_count ?? event.detail?.data?.itemCount ?? 1);
    if (itemCount <= 0) return;
    if (this.hasAttribute('auto-open')) this.open();
  };

  open() {
    const dialog = this.querySelector('dialog');
    if (!dialog?.open) this.showDialog();
  }

  close() {
    const dialog = this.querySelector('dialog');
    if (dialog?.open) this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

(() => {
  if (window.__dolceSingleCartDrawerLoaded) return;
  window.__dolceSingleCartDrawerLoaded = true;

  const DIRECT_SELECTOR = '.dbx-home a[href*="/products/"]';
  const CART_SELECTOR = '.dbx-cart-button,.dbx-announcement-cart';
  const variantPromises = new Map();
  let canonicalDrawer = null;
  let activeRequest = null;
  let cleanupFrame = 0;
  let cartRefreshFrame = 0;

  const style = document.createElement('style');
  style.textContent = `
    cart-drawer-component:not([data-dolce-canonical-cart]){display:none!important}
    cart-drawer-component[data-dolce-canonical-cart]{position:relative!important;z-index:2147483000!important}
    cart-drawer-component[data-dolce-canonical-cart] dialog{max-height:100dvh!important;height:100dvh!important;overflow:hidden!important}
    cart-drawer-component[data-dolce-canonical-cart] .cart-drawer__dialog{max-height:100dvh!important;height:100dvh!important;overflow:hidden!important}
    cart-drawer-component[data-dolce-canonical-cart] .cart-drawer__inner{display:flex!important;flex-direction:column!important;height:100dvh!important;max-height:100dvh!important;min-height:0!important;overflow:hidden!important;box-sizing:border-box!important;padding-bottom:0!important}
    cart-drawer-component[data-dolce-canonical-cart] .cart-drawer__content{flex:1 1 auto!important;min-height:0!important;height:auto!important;overflow-y:auto!important;overflow-x:hidden!important;overscroll-behavior:contain!important;-webkit-overflow-scrolling:touch!important;touch-action:pan-y!important;padding-bottom:max(24px,env(safe-area-inset-bottom))!important}
    cart-drawer-component[data-dolce-canonical-cart] .cart-drawer__items{overflow:visible!important}
    cart-drawer-component[data-dolce-canonical-cart] .cart-drawer__summary{position:static!important;overflow:visible!important;padding-bottom:0!important}
    cart-drawer-component[data-dolce-canonical-cart] .cart-summary-totals{padding-bottom:max(28px,env(safe-area-inset-bottom))!important}
    cart-drawer-component[data-dolce-canonical-cart] [data-dolce-checkout-v2]{position:static!important;right:auto!important;bottom:auto!important;width:100%!important;max-width:none!important;min-height:56px!important;display:flex!important;align-items:center!important;justify-content:center!important;margin:0!important;z-index:auto!important;box-shadow:0 8px 24px rgba(0,0,0,.18)!important;border:1px solid rgba(255,255,255,.12)!important}
    main[data-template*="product"] .entrega-dolce-container,
    main[data-template*="product"] #wrapper-calendario-dolce,
    main[data-template*="product"] shopify-accelerated-checkout,
    main[data-template*="product"] sticky-add-to-cart{display:none!important}
    main[data-template*="product"] .product-form-buttons>.quantity-selector-wrapper{display:none!important}
    main[data-template*="product"] .product-form-buttons>span:has(>add-to-cart-component){flex:1 1 100%!important;width:100%!important}
    main[data-template*="product"] .product-form-buttons add-to-cart-component{display:block!important;width:100%!important}
    main[data-template*="product"] .product-form-buttons .add-to-cart-button{
      width:100%!important;
      min-height:58px!important;
      border:2px solid #f2a7be!important;
      border-radius:12px!important;
      background:#f2a7be!important;
      color:#111!important;
      font-size:16px!important;
      font-weight:900!important;
      letter-spacing:.055em!important;
      box-shadow:0 8px 0 #b94f70,0 14px 26px rgba(0,0,0,.28)!important;
      cursor:pointer!important;
      transition:transform .15s ease,box-shadow .15s ease,background-color .15s ease!important
    }
    main[data-template*="product"] .product-form-buttons .add-to-cart-button *,
    main[data-template*="product"] .product-form-buttons .add-to-cart-button .add-to-cart-text__content{
      color:#111!important;
      -webkit-text-fill-color:#111!important
    }
    main[data-template*="product"] .product-form-buttons .add-to-cart-button:hover{
      background:#ffc2d5!important;
      border-color:#ffc2d5!important;
      transform:translateY(-2px)!important;
      box-shadow:0 10px 0 #b94f70,0 17px 30px rgba(0,0,0,.32)!important
    }
    main[data-template*="product"] .product-form-buttons .add-to-cart-button:active{
      transform:translateY(4px)!important;
      box-shadow:0 3px 0 #b94f70,0 8px 16px rgba(0,0,0,.22)!important
    }
    main[data-template*="product"] .product-form-buttons .add-to-cart-button:focus-visible{
      outline:3px solid #fff!important;
      outline-offset:3px!important
    }
    .dbx-direct-cart-busy{pointer-events:none!important;opacity:.68!important}
    @media(max-width:520px){cart-drawer-component[data-dolce-canonical-cart] .cart-drawer__content{padding-bottom:max(18px,env(safe-area-inset-bottom))!important}}
  `;
  document.head.appendChild(style);

  function releasePageScroll() {
    const lockedTop = document.body.style.top;
    const restoreY = lockedTop ? Math.max(0, Math.abs(Number.parseFloat(lockedTop) || 0)) : null;

    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');

    if (restoreY !== null) {
      window.scrollTo({ top:restoreY, behavior:'instant' });
    }
  }

  function unlockPageWhenClosed() {
    const openDialog = document.querySelector('cart-drawer-component[data-dolce-canonical-cart] dialog[open]');
    if (openDialog) return;
    releasePageScroll();
  }

  function canonicalizeDrawers() {
    const drawers = Array.from(document.querySelectorAll('cart-drawer-component'));
    if (!canonicalDrawer || !canonicalDrawer.isConnected) {
      canonicalDrawer = drawers.find((drawer) => drawer.hasAttribute('data-dolce-canonical-cart')) || drawers[0] || null;
    }
    if (!canonicalDrawer) return null;

    canonicalDrawer.setAttribute('data-dolce-canonical-cart','true');
    if (canonicalDrawer.parentElement !== document.body) document.body.appendChild(canonicalDrawer);

    drawers.forEach((drawer) => {
      if (drawer === canonicalDrawer) return;
      try { drawer.close?.(); } catch (_) {}
      drawer.remove();
    });

    canonicalDrawer.querySelector('dialog')?.addEventListener('close', unlockPageWhenClosed, { once:false });
    return canonicalDrawer;
  }

  function scheduleCanonicalize() {
    if (cleanupFrame) return;
    cleanupFrame = requestAnimationFrame(() => {
      cleanupFrame = 0;
      canonicalizeDrawers();
    });
  }

  const observer = new MutationObserver(scheduleCanonicalize);
  observer.observe(document.documentElement, { childList:true, subtree:true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleCanonicalize, { once:true });
  else scheduleCanonicalize();
  window.addEventListener('pagehide', releasePageScroll);
  window.addEventListener('pageshow', () => {
    scheduleCanonicalize();
    requestAnimationFrame(unlockPageWhenClosed);
  });

  function fetchWithTimeout(url, options = {}, timeout = 10000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    return fetch(url, { ...options, signal:controller.signal }).finally(() => clearTimeout(timer));
  }

  function getSectionIds() {
    return [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map((item) => item.dataset.sectionId).filter(Boolean))];
  }

  function replaceDrawerFromSections(sections) {
    const current = canonicalizeDrawers();
    if (!current || !sections) return current;

    for (const html of Object.values(sections)) {
      if (typeof html !== 'string' || !html.includes('cart-drawer-component')) continue;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('cart-drawer-component');
      if (!fresh) continue;

      fresh.setAttribute('data-dolce-canonical-cart', 'true');

      const currentDialog = current.querySelector('dialog');
      if (currentDialog?.open) {
        currentDialog.close();
        releasePageScroll();
      }

      current.replaceWith(fresh);
      canonicalDrawer = fresh;
      canonicalDrawer.querySelector('dialog')?.addEventListener('close', unlockPageWhenClosed, { once:false });
      return canonicalDrawer;
    }

    return current;
  }

  async function loadDrawerSections() {
    const sectionIds = getSectionIds();
    if (!sectionIds.length) return {};
    const separator = location.search ? '&' : '?';
    const response = await fetchWithTimeout(`${location.pathname}${location.search}${separator}sections=${encodeURIComponent(sectionIds.join(','))}`, {
      credentials:'same-origin',
      cache:'no-store',
      headers:{ Accept:'application/json' }
    });
    if (!response.ok) throw new Error('No se pudo actualizar el carrito lateral');
    return response.json();
  }

  function refreshDrawerAfterCartChange(event) {
    if (cartRefreshFrame) cancelAnimationFrame(cartRefreshFrame);
    cartRefreshFrame = requestAnimationFrame(async () => {
      cartRefreshFrame = 0;
      const current = canonicalizeDrawers();
      const wasOpen = Boolean(current?.querySelector('dialog[open]'));
      const previousScroll = current?.querySelector('.cart-drawer__content')?.scrollTop || 0;

      try {
        let sections = event.detail?.data?.sections || {};
        const hasDrawer = Object.values(sections).some((html) => typeof html === 'string' && html.includes('cart-drawer-component'));
        if (!hasDrawer) sections = await loadDrawerSections();

        const refreshed = replaceDrawerFromSections(sections);
        if (!refreshed) return;

        const itemCount = Number(event.detail?.resource?.item_count ?? event.detail?.data?.itemCount ?? 0);
        document.querySelectorAll('.dbx-cart-count').forEach((count) => {
          count.textContent = String(itemCount);
        });

        if (itemCount <= 0) {
          refreshed.close?.();
          releasePageScroll();
          requestAnimationFrame(unlockPageWhenClosed);
          setTimeout(unlockPageWhenClosed, 350);
          return;
        }

        if (!wasOpen) return;
        refreshed.open();
        requestAnimationFrame(() => {
          const content = refreshed.querySelector('.cart-drawer__content');
          if (content) content.scrollTop = Math.min(previousScroll, Math.max(0, content.scrollHeight - content.clientHeight));
        });
      } catch (error) {
        console.error('Dolce cart refresh:', error);
      }
    });
  }

  async function resolveVariantId(link) {
    const url = new URL(link.href, location.origin);
    const explicit = link.dataset.variantId || url.searchParams.get('variant');
    if (explicit) return String(explicit);
    const path = url.pathname.replace(/\/$/, '');
    if (!path.includes('/products/')) throw new Error('Enlace sin producto');
    if (!variantPromises.has(path)) {
      variantPromises.set(path, fetchWithTimeout(`${path}.js`, { credentials:'same-origin', cache:'no-store', headers:{ Accept:'application/json' } })
        .then(async (response) => {
          if (!response.ok) throw new Error('No se pudo cargar el producto');
          const product = await response.json();
          const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
          if (!variant?.id) throw new Error('Sin variante disponible');
          return String(variant.id);
        })
        .catch((error) => { variantPromises.delete(path); throw error; }));
    }
    return variantPromises.get(path);
  }

  async function addOneItem(variantId) {
    const drawer = canonicalizeDrawers();
    if (!drawer) throw new Error('No se encontró el carrito');
    drawer.open();
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    const sectionIds = getSectionIds();
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', location.pathname);

    const response = await fetchWithTimeout(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method:'POST', body:formData, credentials:'same-origin', cache:'no-store', headers:{ Accept:'application/json' }
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'No se pudo agregar');

    replaceDrawerFromSections(result.sections || {})?.open();

    const cartResponse = await fetchWithTimeout('/cart.js', { credentials:'same-origin', cache:'no-store', headers:{ Accept:'application/json' } });
    if (!cartResponse.ok) throw new Error('No se pudo actualizar el carrito');
    const cart = await cartResponse.json();
    document.querySelectorAll('.dbx-cart-count').forEach((count) => {
      count.textContent = String(cart.item_count || 0);
    });
    document.dispatchEvent(new CartAddEvent(cart, `dolce-${variantId}`, {
      source:'product-form-component', itemCount:1, variantId, sections:result.sections || {}
    }));
    await customElements.whenDefined('cart-drawer-component');
    requestAnimationFrame(() => canonicalizeDrawers()?.open());
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const addButton = event.target.closest('main[data-template*="product"] button[name="add"]');
    if (!(addButton instanceof HTMLButtonElement) || addButton.disabled) return;
    canonicalizeDrawers()?.open();
  }, true);

  document.addEventListener(CartAddEvent.eventName, async (event) => {
    if (String(event.detail?.sourceId || '').startsWith('dolce-')) return;
    if (event.detail?.data?.source !== 'product-form-component') return;

    try {
      let sections = event.detail?.data?.sections || {};
      const hasDrawer = Object.values(sections).some((html) => typeof html === 'string' && html.includes('cart-drawer-component'));
      if (!hasDrawer) sections = await loadDrawerSections();
      replaceDrawerFromSections(sections)?.open();
    } catch (error) {
      console.error('Dolce product cart:', error);
      canonicalizeDrawers()?.open();
    }
  });

  document.addEventListener(CartAddEvent.eventName, (event) => {
    if (event.detail?.data?.source !== 'cart-items-component') return;
    refreshDrawerAfterCartChange(event);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const removeButton = event.target.closest(
      'cart-drawer-component[data-dolce-canonical-cart] .cart-items__remove'
    );
    if (!(removeButton instanceof HTMLButtonElement)) return;

    const drawer = removeButton.closest('cart-drawer-component[data-dolce-canonical-cart]');
    if (!drawer || drawer.querySelectorAll('.cart-items__remove').length !== 1) return;

    const dialog = drawer.querySelector('dialog');
    if (dialog?.open) dialog.close();
    releasePageScroll();
  }, true);

  document.addEventListener('click', async (event) => {
    const cartLink = event.target.closest(CART_SELECTOR);
    if (cartLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      event.stopPropagation();
      canonicalizeDrawers()?.open();
      return;
    }

    const link = event.target.closest(DIRECT_SELECTOR);
    if (!(link instanceof HTMLAnchorElement)) return;
    const destination = new URL(link.href, location.origin);
    if (!destination.pathname.includes('/products/')) return;

    event.preventDefault();
    event.stopPropagation();
    if (activeRequest || link.dataset.directCartBusy === 'true') return;

    link.dataset.directCartBusy = 'true';
    link.classList.add('dbx-direct-cart-busy');
    const originalText = link.textContent;
    const textButton = link.matches('.dbx-primary-cta,.dbx-card-cta');
    if (textButton) link.textContent = 'AGREGANDO…';
    canonicalizeDrawers()?.open();

    try {
      activeRequest = addOneItem(await resolveVariantId(link));
      await activeRequest;
    } catch (error) {
      console.error('Dolce cart:', error);
      location.assign(link.href);
    } finally {
      activeRequest = null;
      delete link.dataset.directCartBusy;
      link.classList.remove('dbx-direct-cart-busy');
      if (textButton && link.isConnected) link.textContent = originalText;
    }
  }, true);
})();
